import { useEffect, useRef, type FunctionComponent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Checkbox, Drawer, Form, Input, InputNumber, Select, Space, message } from "antd";

import {
  asBadgeCategory,
  asBadgeConfig,
  asBadgeRuleType,
  BadgeCategory,
  BadgeRuleType,
  type BadgeActivityCategory,
  type BadgeChallengeKind,
  type BadgeConfig,
  type BadgeDefinitionEntity,
} from "@fitness-app/data-models/entities/Badge";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { supabase } from "~/store/initializeStore";
import {
  badgeActivityCategoryOptions,
  badgeCategoryOptions,
  badgeChallengeKindOptions,
  badgeColorOptions,
  badgeIconOptions,
  badgeRuleTypeOptions,
} from "./constants";
import { areTiersInputTokensValid, parseTiersInput, slugifyBadgeKey, tiersToInput } from "./helpers";
import EmojiPicker from "~/components/EmojiPicker/EmojiPicker";

type Props = {
  open: boolean;
  badge: BadgeDefinitionEntity | null;
  onClose: () => void;
  onSaved: () => void;
};

type BadgeFormValues = {
  name: string;
  key: string;
  description?: string;
  icon?: string;
  color: string;
  category: BadgeCategory;
  ruleType: BadgeRuleType;
  activityCategory?: BadgeActivityCategory;
  tiers?: string;
  challengeKind?: BadgeChallengeKind;
  /** TRE-1582 — scope a challenge badge: all challenges / one specific / one recurring template. */
  challengeScope?: ChallengeScope;
  challengeId?: string;
  challengeTemplateId?: string;
  /** TRE-1762 — czy ta odznaka zastępuje ogólną odznakę tego samego rodzaju w swoim zakresie. */
  suppressGlobalBadge?: boolean;
  sortOrder?: number;
};

type ChallengeScope = "global" | "challenge" | "template";

const challengeScopeOptions = [
  { value: "global", label: "Wszystkie wyzwania (globalna)" },
  { value: "challenge", label: "Konkretne wyzwanie" },
  { value: "template", label: "Szablon cykliczny (wszystkie edycje)" },
];

const hasActivityCategory = (ruleType?: BadgeRuleType): boolean =>
  ruleType === BadgeRuleType.FirstEvent || ruleType === BadgeRuleType.Counter;

const hasTiers = (ruleType?: BadgeRuleType): boolean =>
  ruleType === BadgeRuleType.Counter || ruleType === BadgeRuleType.HabitStreak;

// nutritionReports is awarded at the event source (the generate-nutrition-report endpoint), not
// by the activity sweep — a counter badge on it would never progress, so it is firstEvent-only.
const isCategoryAllowed = (ruleType: BadgeRuleType, category: BadgeActivityCategory): boolean =>
  category !== "nutritionReports" || ruleType === BadgeRuleType.FirstEvent;

const buildConfig = (values: BadgeFormValues): BadgeConfig => {
  const config: BadgeConfig = {};

  if (
    hasActivityCategory(values.ruleType) &&
    values.activityCategory &&
    isCategoryAllowed(values.ruleType, values.activityCategory)
  ) {
    config.category = values.activityCategory;
  }

  if (hasTiers(values.ruleType)) {
    config.tiers = parseTiersInput(values.tiers);
  }

  if (values.ruleType === BadgeRuleType.Challenge && values.challengeKind) {
    config.kind = values.challengeKind;
    if (values.challengeScope === "challenge" && values.challengeId) {
      config.challengeId = values.challengeId;
    } else if (values.challengeScope === "template" && values.challengeTemplateId) {
      config.challengeTemplateId = values.challengeTemplateId;
    }

    // Zapisujemy tylko dla odznak przypiętych — dla globalnej nie ma czego wypierać.
    if (values.challengeScope !== "global") {
      config.suppressGlobalBadge = values.suppressGlobalBadge !== false;
    }
  }

  return config;
};

const toFormValues = (badge: BadgeDefinitionEntity): BadgeFormValues => {
  const config = asBadgeConfig(badge.config);

  return {
    name: badge.name,
    key: badge.key,
    description: badge.description ?? undefined,
    icon: badge.icon,
    color: badge.color,
    category: asBadgeCategory(badge.category),
    ruleType: asBadgeRuleType(badge.ruleType),
    activityCategory: config.category,
    tiers: tiersToInput(config),
    challengeKind: config.kind,
    challengeScope: config.challengeId ? "challenge" : config.challengeTemplateId ? "template" : "global",
    challengeId: config.challengeId,
    challengeTemplateId: config.challengeTemplateId,
    // Brak pola = wypieranie włączone, tak samo jak czyta to silnik odznak.
    suppressGlobalBadge: config.suppressGlobalBadge !== false,
    sortOrder: badge.sortOrder,
  };
};

const defaultValues: Partial<BadgeFormValues> = {
  color: "gold",
  category: BadgeCategory.Activity,
  ruleType: BadgeRuleType.FirstEvent,
  challengeScope: "global",
  suppressGlobalBadge: true,
  sortOrder: 100,
};

export const BadgeFormDrawer: FunctionComponent<Props> = ({ open, badge, onClose, onSaved }) => {
  const [form] = Form.useForm<BadgeFormValues>();
  const queryClient = useQueryClient();
  const { memberId } = useUserClaims();
  const isEdit = Boolean(badge);
  const keyTouchedRef = useRef(false);

  const ruleTypeValue = Form.useWatch("ruleType", form);
  const challengeScopeValue = Form.useWatch("challengeScope", form);

  // Challenge/template pickers — only fetched while a challenge badge is being edited.
  const isChallengeRule = ruleTypeValue === BadgeRuleType.Challenge;

  const challengesQuery = useQuery({
    queryKey: ["badge-scope-challenges"],
    enabled: open && isChallengeRule,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge")
        .select("id, name, startDate, status")
        .order("startDate", { ascending: false })
        .returns<{ id: string; name: string; startDate: string; status: string }[]>();
      if (error) {
        throw error;
      }
      return data ?? [];
    },
  });

  const templatesQuery = useQuery({
    queryKey: ["badge-scope-templates"],
    enabled: open && isChallengeRule,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_template")
        .select("id, name")
        .eq("archived", false)
        .order("name")
        .returns<{ id: string; name: string }[]>();
      if (error) {
        throw error;
      }
      return data ?? [];
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    keyTouchedRef.current = false;

    if (badge) {
      form.setFieldsValue(toFormValues(badge));
    } else {
      form.resetFields();
      form.setFieldsValue(defaultValues);
    }
  }, [open, badge, form]);

  const saveBadgeMutation = useMutation({
    mutationFn: async (values: BadgeFormValues) => {
      const payload = {
        name: values.name,
        description: values.description?.trim() ? values.description.trim() : null,
        icon: values.icon?.trim() ?? "",
        color: values.color,
        category: values.category,
        ruleType: values.ruleType,
        config: buildConfig(values),
        sortOrder: values.sortOrder ?? 100,
      };

      if (badge) {
        const { error } = await supabase
          .from("badge_definition")
          .update({ ...payload, updatedAt: new Date().toISOString() } as never)
          .eq("id", badge.id);

        if (error) {
          throw new Error(error.message);
        }

        return;
      }

      const { error } = await supabase.from("badge_definition").insert({
        ...payload,
        key: values.key,
        createdBy: memberId ?? null,
      } as never);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success(isEdit ? "Odznaka zaktualizowana" : "Odznaka utworzona");
      void queryClient.invalidateQueries({ queryKey: ["badges-list"] });
      form.resetFields();
      onSaved();
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się zapisać odznaki: ${error.message}`);
    },
  });

  const onValuesChange = (changed: Partial<BadgeFormValues>) => {
    // Auto-suggest the key slug from the name until the trainer edits the key manually (create only).
    if (!isEdit && changed.name !== undefined && !keyTouchedRef.current) {
      form.setFieldValue("key", slugifyBadgeKey(changed.name ?? ""));
    }
  };

  return (
    <Drawer
      title={isEdit ? "Edytuj odznakę" : "Nowa odznaka"}
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Anuluj</Button>
          <Button type="primary" loading={saveBadgeMutation.isLoading} onClick={() => form.submit()}>
            {isEdit ? "Zapisz" : "Utwórz"}
          </Button>
        </Space>
      }
    >
      <Form<BadgeFormValues>
        form={form}
        layout="vertical"
        initialValues={defaultValues}
        onValuesChange={onValuesChange}
        onFinish={(values) => saveBadgeMutation.mutate(values)}
      >
        <Form.Item name="name" label="Nazwa" rules={[{ required: true, message: "Podaj nazwę odznaki" }]}>
          <Input placeholder="np. Pierwszy trening" />
        </Form.Item>

        <Form.Item
          name="key"
          label="Klucz (slug)"
          tooltip="Unikalny identyfikator odznaki — nie można go zmienić po utworzeniu."
          rules={
            isEdit
              ? []
              : [
                  { required: true, message: "Podaj klucz odznaki" },
                  {
                    pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                    message: "Dozwolone są małe litery, cyfry i myślniki",
                  },
                ]
          }
        >
          <Input
            placeholder="np. pierwszy-trening"
            disabled={isEdit}
            onChange={() => {
              keyTouchedRef.current = true;
            }}
          />
        </Form.Item>

        <Form.Item name="description" label="Opis">
          <Input.TextArea rows={3} placeholder="Krótki opis widoczny dla klientki" />
        </Form.Item>

        <Form.Item name="icon" label="Ikona (emoji)">
          <EmojiPicker options={badgeIconOptions} />
        </Form.Item>
        <Form.Item name="color" label="Kolor" rules={[{ required: true }]}>
          <Select options={badgeColorOptions} style={{ width: 200 }} />
        </Form.Item>

        <Form.Item name="category" label="Kategoria" rules={[{ required: true, message: "Wybierz kategorię" }]}>
          <Select options={badgeCategoryOptions} />
        </Form.Item>

        <Form.Item name="ruleType" label="Reguła" rules={[{ required: true, message: "Wybierz regułę" }]}>
          <Select options={badgeRuleTypeOptions} />
        </Form.Item>

        {hasActivityCategory(ruleTypeValue) ? (
          <Form.Item
            name="activityCategory"
            label="Kategoria aktywności"
            rules={[
              { required: true, message: "Wybierz kategorię aktywności" },
              {
                validator: (_rule, value: BadgeActivityCategory | undefined) =>
                  !value || !ruleTypeValue || isCategoryAllowed(ruleTypeValue, value)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Raport żywieniowy AI działa tylko z regułą „Pierwsze zdarzenie”")),
              },
            ]}
          >
            <Select
              options={badgeActivityCategoryOptions.filter(
                (option) => !ruleTypeValue || isCategoryAllowed(ruleTypeValue, option.value),
              )}
            />
          </Form.Item>
        ) : null}

        {hasTiers(ruleTypeValue) ? (
          <Form.Item
            name="tiers"
            label="Progi (tiery)"
            tooltip="Rosnące progi oddzielone przecinkami — osiągnięcie kolejnego progu podnosi tier odznaki."
            rules={[
              {
                validator: (_rule, value: string | undefined) =>
                  areTiersInputTokensValid(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Podaj przynajmniej jeden próg, np. 10, 50, 100")),
              },
            ]}
          >
            <Input placeholder="np. 10, 50, 100" />
          </Form.Item>
        ) : null}

        {ruleTypeValue === BadgeRuleType.Challenge ? (
          <>
            <Form.Item name="challengeKind" label="Rodzaj" rules={[{ required: true, message: "Wybierz rodzaj" }]}>
              <Select options={badgeChallengeKindOptions} />
            </Form.Item>
            <Form.Item
              name="challengeScope"
              label="Zakres"
              tooltip="Globalna = za dowolne wyzwanie tego rodzaju. Możesz też przypiąć odznakę do jednego wyzwania albo do wszystkich edycji cyklicznego wyzwania."
            >
              <Select options={challengeScopeOptions} />
            </Form.Item>
            {challengeScopeValue === "challenge" ? (
              <Form.Item name="challengeId" label="Wyzwanie" rules={[{ required: true, message: "Wybierz wyzwanie" }]}>
                <Select
                  showSearch
                  loading={challengesQuery.isFetching}
                  optionFilterProp="label"
                  placeholder="Wyszukaj wyzwanie"
                  options={(challengesQuery.data ?? []).map((challenge) => ({
                    value: challenge.id,
                    label: `${challenge.name} (${challenge.startDate?.slice(0, 10) ?? "—"})`,
                  }))}
                />
              </Form.Item>
            ) : null}
            {challengeScopeValue === "template" ? (
              <Form.Item
                name="challengeTemplateId"
                label="Szablon wyzwania"
                rules={[{ required: true, message: "Wybierz szablon" }]}
              >
                <Select
                  showSearch
                  loading={templatesQuery.isFetching}
                  optionFilterProp="label"
                  placeholder="Wyszukaj szablon cykliczny"
                  notFoundContent="Brak szablonów cyklicznych"
                  options={(templatesQuery.data ?? []).map((template) => ({
                    value: template.id,
                    label: template.name,
                  }))}
                />
              </Form.Item>
            ) : null}
            {challengeScopeValue !== "global" ? (
              <Form.Item
                name="suppressGlobalBadge"
                valuePropName="checked"
                tooltip="Bez tego klientka dostanie obie odznaki naraz: tę dedykowaną i ogólną tego samego rodzaju. Wypieranie działa tylko w obrębie tego samego rodzaju — odznaka za próg nie zabierze odznaki za podium."
              >
                <Checkbox>Nie przyznawaj ogólnej odznaki dla tego wyzwania</Checkbox>
              </Form.Item>
            ) : null}
          </>
        ) : null}

        <Form.Item name="sortOrder" label="Kolejność sortowania">
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default BadgeFormDrawer;
